import { useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/icon";
import SearchField from "@/components/search-input";
import { VitrinCategory } from "@/models/home/VitrinCategory";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import { cn } from "@/lib/cn";
import { ELEMENT_TYPES } from "@/features/analytics";
import CategoryFilterDrawer from "./category-filter-drawer";
import FacetFilterDrawer, {
  type FilterToggle,
} from "./facet-filter-drawer";
import type { SearchFacets } from "@/models/search/SearchFacets";
import "swiper/css";
import "swiper/css/free-mode";
import { SafeArea } from "@/components/top-bar";
import type { FacetBucket } from "@/models/search/SearchFacets";

// Sort options. `-users_count` powers "most popular"; the backend falls back to
// its default ordering if it doesn't recognize the key, so it's safe to offer.
// `price` sorts by what the buyer actually pays (the discounted price), which
// is the same basis the price filters use.
export const SORT_OPTIONS = [
  { value: "relevance", label: "مرتبط‌ترین" },
  { value: "-created_at", label: "جدیدترین" },
  { value: "created_at", label: "قدیمی‌ترین" },
  { value: "-users_count", label: "محبوب‌ترین" },
  { value: "price", label: "ارزان‌ترین" },
  { value: "-price", label: "گران‌ترین" },
] as const;

/** Search ranking. Also valid without a query, where it means "best first". */
export const RELEVANCE_SORT = "relevance";

/** The order to browse in when there is no query to rank against. */
export const DEFAULT_SORT = "-created_at";

function getSortLabel(value: string): string {
  return SORT_OPTIONS.find((o) => o.value === value)?.label ?? "مرتب‌سازی";
}

interface Props {
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  onSubmit: (value: string) => void;
  loading?: boolean;
  /** Quick filters only make sense once there's an active search. */
  showFilters: boolean;
  categories: VitrinCategory[];
  selectedCategoryIds: number[];
  onToggleCategory: (id: number) => void;
  onClearCategories: () => void;
  sortOrder: string;
  onSortChange: (value: string) => void;
  discountOnly: boolean;
  onToggleDiscount: () => void;
  freeOnly: boolean;
  onToggleFree: () => void;
  /** How many free courses the other active filters leave. Undefined = no counts. */
  freeCount?: number;
  /** Per-category counts, for the drawer. Undefined = no counts. */
  categoryCounts?: FacetBucket<number>[];
  /**
   * Toggles the API only understands once its search backend is deployed. The
   * caller decides whether to pass any — an empty list renders nothing, which
   * is what an API without those filters must get.
   */
  facetChips?: FacetChipSpec[];
  /** Drives the list-shaped filter drawer. Null hides it entirely. */
  facets?: SearchFacets | null;
  facetSelections?: Record<string, string[]>;
  onToggleFacetValue?: (param: string, value: string) => void;
  onClearFacetValues?: () => void;
  facetSelectionCount?: number;
  /** Boolean filters, mirrored into the drawer as switches. */
  facetToggles?: FilterToggle[];
}

export interface FacetChipSpec {
  key: string;
  label: string;
  icon: string;
  active: boolean;
  /** From the matching facet bucket; omitted when the API sent no counts. */
  count?: number;
  onToggle: () => void;
}

// A count rides along inside the chip it belongs to. Rendered only when the API
// sent facets — there is no placeholder, because a chip with no number beside
// it is the honest look when counts are unavailable.
function ChipCount({ value }: { value?: number }) {
  if (typeof value !== "number") return null;
  return (
    <span className="text-[10px] font-normal text-default-400">
      {value.toLocaleString("fa-IR")}
    </span>
  );
}

// Shared chip styling — primary-tinted when active, neutral outline otherwise.
const chipClass = (active: boolean) =>
  cn(
    "flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border whitespace-nowrap transition flex-shrink-0",
    active
      ? "text-primary border-primary bg-primary/10"
      : "text-default-600 border-default-300 bg-background",
  );

export default function SearchFilterBar({
  searchQuery,
  setSearchQuery,
  onSubmit,
  loading,
  showFilters,
  categories,
  selectedCategoryIds,
  onToggleCategory,
  onClearCategories,
  sortOrder,
  onSortChange,
  discountOnly,
  onToggleDiscount,
  freeOnly,
  onToggleFree,
  freeCount,
  categoryCounts,
  facetChips,
  facets,
  facetSelections,
  onToggleFacetValue,
  onClearFacetValues,
  facetSelectionCount = 0,
  facetToggles,
}: Props) {
  const navigate = useNavigate();
  const [sortOpen, setSortOpen] = useState(false);

  return (
    <div className="sticky top-0 z-30 bg-secondary-background/95 backdrop-blur-sm border-b border-default-100">
      <SafeArea />
      <div className="flex items-center p-4">
        {/* <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="بازگشت"
          className="shrink-0 text-default-700 overflow-hidden w-7 me-2 search-back-in"
        >
          <Icon name="arrow-right" size={22} />
        </button> */}
        <SearchField
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onSubmit={onSubmit}
          loading={loading}
          trackSection="search_filter_bar"
        />
      </div>

      {showFilters && (
        <div className="px-4 pb-3" data-track-section="search_filters">
          <Swiper
            spaceBetween={8}
            slidesPerView="auto"
            modules={[FreeMode]}
            freeMode={{ enabled: true, momentum: true }}
            className="!m-0"
          >
            {/* Sort drawer trigger */}
            <SwiperSlide className="!w-auto">
              <Drawer open={sortOpen} onOpenChange={setSortOpen}>
                <DrawerTrigger asChild>
                  <button
                    type="button"
                    className={chipClass(false)}
                    data-track-type={ELEMENT_TYPES.filterChip}
                    data-track-id="sort"
                  >
                    <Icon name="sort-descending" size={16} />
                    {getSortLabel(sortOrder)}
                  </button>
                </DrawerTrigger>
                <DrawerContent className="mt-24">
                  <DrawerTitle className="mb-4">مرتب‌سازی</DrawerTitle>
                  {/* The drawer portals to <body>, so it declares its own
                      section instead of inheriting the bar's. */}
                  <div className="space-y-2" data-track-section="sort_options">
                    {SORT_OPTIONS.map((option) => {
                      const active = sortOrder === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            onSortChange(option.value);
                            setSortOpen(false);
                          }}
                          data-track-type={ELEMENT_TYPES.filterChip}
                          data-track-id={option.value}
                          className={cn(
                            "w-full flex items-center justify-between text-right p-3 rounded-lg text-sm font-medium border transition",
                            active
                              ? "text-primary border-primary bg-primary/10"
                              : "text-default-700 border-default-200 bg-background",
                          )}
                        >
                          {option.label}
                          {active && (
                            <Icon name="check-circle-fill" size={18} className="text-primary" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </DrawerContent>
              </Drawer>
            </SwiperSlide>

            {facets && onToggleFacetValue && onClearFacetValues && (
              <SwiperSlide className="!w-auto">
                <FacetFilterDrawer
                  facets={facets}
                  selected={facetSelections ?? {}}
                  onToggle={onToggleFacetValue}
                  onClearAll={onClearFacetValues}
                  activeCount={facetSelectionCount}
                  toggles={facetToggles}
                />
              </SwiperSlide>
            )}


            {/* Category drawer trigger (categories + their subcategories) */}
            <SwiperSlide className="!w-auto">
              <CategoryFilterDrawer
                categories={categories}
                selectedCategoryIds={selectedCategoryIds}
                onToggleCategory={onToggleCategory}
                onClearCategories={onClearCategories}
                categoryCounts={categoryCounts}
              />
            </SwiperSlide>


            {/* Quick filters */}
            <SwiperSlide className="!w-auto">
              <button
                type="button"
                onClick={onToggleDiscount}
                className={chipClass(discountOnly)}
                data-track-type={ELEMENT_TYPES.filterChip}
                data-track-id="discount"
              >
                <Icon name="discount" size={16} />
                تخفیف‌دار
              </button>
            </SwiperSlide>
            <SwiperSlide className="!w-auto">
              <button
                type="button"
                onClick={onToggleFree}
                className={chipClass(freeOnly)}
                data-track-type={ELEMENT_TYPES.filterChip}
                data-track-id="free"
              >
                <Icon name="free-price" size={16} />
                رایگان
                <ChipCount value={freeCount} />
              </button>
            </SwiperSlide>

            {facetChips?.map((chip) => (
              <SwiperSlide key={chip.key} className="!w-auto">
                <button
                  type="button"
                  onClick={chip.onToggle}
                  className={chipClass(chip.active)}
                  data-track-type={ELEMENT_TYPES.filterChip}
                  data-track-id={chip.key}
                >
                  <Icon name={chip.icon} size={16} />
                  {chip.label}
                  <ChipCount value={chip.count} />
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </div>
  );
}
